// src/pages/subscription/TenantSubscriptionDetailPage.tsx

import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { observer } from '@react';
import { useAuth, useSubscription, useI18n } from '@react/hooks';
import {
  Button,
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Alert,
  Badge,
  ErrorAlert,
} from '@react/components';

function formatLabel(value: string, prefix: string): string {
  return value.replace(prefix, '').split('_').map(w => w.charAt(0) + w.slice(1).toLowerCase()).join(' ');
}

function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) return '--';
  try { return new Date(dateStr).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }); }
  catch { return String(dateStr); }
}

function formatMoney(amount: number | null | undefined): string {
  if (amount == null) return '--';
  return (amount / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function statusVariant(status: string): 'success' | 'warning' | 'error' | 'primary' | 'secondary' {
  const s = (status || '').replace('TENANT_SUBSCRIPTION_STATUS_', '');
  switch (s) {
    case 'ACTIVE': return 'success';
    case 'TRIALING': case 'PENDING': return 'primary';
    case 'PAST_DUE': case 'PAUSED': return 'warning';
    case 'CANCELED': case 'EXPIRED': case 'SUSPENDED': return 'error';
    default: return 'secondary';
  }
}

export const TenantSubscriptionDetailPage = observer(() => {
  // Tenant subscriptions are keyed by (tenant, product); the route param is the product id
  // and the tenant comes from the authenticated user.
  const { id } = useParams<{ id: string }>();
  const store = useSubscription();
  const auth = useAuth();
  const { t } = useI18n();
  const tenantId = auth.user?.entity?.tenant_id || '';
  const [item, setItem] = useState<any>(null);

  const loadSubscription = (productId: string) => {
    if (!tenantId) return;
    store.GetTenantSubscription(tenantId, productId)
      .then((res) => setItem(res?.tenant ?? null))
      .catch(() => {});
  };

  useEffect(() => {
    if (id) loadSubscription(id);
    return () => { setItem(null); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, tenantId]);

  const handleCancel = async () => {
    if (!id || !tenantId) return;
    if (!confirm(t('subscription.cancelTenantSubscriptionConfirm'))) return;
    try {
      await store.CancelTenantSubscription(tenantId, id);
      loadSubscription(id);
    } catch {
      // Error handled by store
    }
  };

  if (store.isLoading) {
    return <div className="flex items-center justify-center h-64"><div className="text-muted-foreground">{t('common.loading')}</div></div>;
  }
  if (store.error) {
    return <><ErrorAlert error={store.error} /><Link to="/subscriptions/tenants" className="mt-2 inline-block text-sm underline text-primary mt-2 inline-block text-sm underline text-primary">{t("common.backToList")}</Link></>;
  }
  if (!item) {
    return <Alert variant="warning" title={t('common.notFound')}><Link to="/subscriptions/tenants" className="mt-2 inline-block text-sm underline text-primary">{t('common.backToList')}</Link></Alert>;
  }

  const features: string[] = item.features ?? [];
  const isCancelable = item.status && !['TENANT_SUBSCRIPTION_STATUS_CANCELED', 'TENANT_SUBSCRIPTION_STATUS_EXPIRED'].includes(item.status);

  return (
    <div className="space-y-6">
      <nav className="text-sm">
        <Link to="/subscriptions" className="text-muted-foreground hover:text-foreground">Subscriptions</Link>
        <span className="mx-2 text-muted-foreground">/</span>
        <Link to="/subscriptions/tenants" className="text-muted-foreground hover:text-foreground">Tenant Subscriptions</Link>
        <span className="mx-2 text-muted-foreground">/</span>
        <span className="text-foreground">{String(item.tenant_name ?? item.tenant_id ?? id)}</span>
      </nav>

      {/* The tab already names this page; the heading stays for screen readers,
          the tenant and product line below still identifies the record. */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="sr-only">Tenant Subscription</h1>
          {item.status && <Badge variant={statusVariant(item.status)} size="sm">{formatLabel(item.status, 'TENANT_SUBSCRIPTION_STATUS_')}</Badge>}
          <p className="text-sm text-muted-foreground mt-1">
            {String(item.tenant_name ?? item.tenant_id ?? '')} &middot; {String(item.product_name ?? item.product_id ?? '')}
          </p>
        </div>
        <div className="flex gap-2">
          <Link to={`/subscriptions/tenants/${id}/edit`}><Button variant="secondary">{t('common.edit')}</Button></Link>
          {isCancelable && (
            <Button variant="danger" onClick={handleCancel}>Cancel Subscription</Button>
          )}
        </div>
      </div>

      {/* Subscription Info */}
      <Card>
        <CardHeader><CardTitle>Subscription Info</CardTitle></CardHeader>
        <CardContent>
          <dl className="divide-y divide-border">
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Tenant</dt>
              <dd className="text-sm text-foreground col-span-2">{String(item.tenant_name ?? item.tenant_id ?? '--')}</dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Product</dt>
              <dd className="text-sm text-foreground col-span-2">{String(item.product_name ?? item.product_id ?? '--')}</dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Plan</dt>
              <dd className="text-sm text-foreground col-span-2">{String(item.plan_name ?? item.plan_id ?? '--')}</dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Status</dt>
              <dd className="text-sm col-span-2">
                {item.status ? <Badge variant={statusVariant(item.status)} size="sm">{formatLabel(item.status, 'TENANT_SUBSCRIPTION_STATUS_')}</Badge> : '--'}
              </dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Seats</dt>
              <dd className="text-sm text-foreground col-span-2">{item.seats != null ? String(item.seats) : '--'}</dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Price</dt>
              <dd className="text-sm text-foreground col-span-2">{formatMoney(item.price ?? item.current_price)}</dd>
            </div>
          </dl>
        </CardContent>
      </Card>

      {/* Current Period */}
      <Card>
        <CardHeader><CardTitle>Current Period</CardTitle></CardHeader>
        <CardContent>
          <dl className="divide-y divide-border">
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Period Start</dt>
              <dd className="text-sm text-foreground col-span-2">{formatDate(item.current_period_start)}</dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Period End</dt>
              <dd className="text-sm text-foreground col-span-2">{formatDate(item.current_period_end)}</dd>
            </div>
          </dl>
        </CardContent>
      </Card>

      {/* Features */}
      {features.length > 0 && (
        <Card>
          <CardHeader><CardTitle>Features</CardTitle></CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {features.map((feature: string, i: number) => (
                <Badge key={i} variant="secondary" size="sm">{feature}</Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Config */}
      {item.config && (
        <Card>
          <CardHeader><CardTitle>Configuration</CardTitle></CardHeader>
          <CardContent>
            <pre className="text-sm text-foreground bg-muted rounded-md p-4 overflow-auto font-mono">
              {typeof item.config === 'string' ? item.config : JSON.stringify(item.config, null, 2)}
            </pre>
          </CardContent>
        </Card>
      )}

      {/* Timestamps */}
      <Card>
        <CardHeader><CardTitle>Timestamps</CardTitle></CardHeader>
        <CardContent>
          <dl className="divide-y divide-border">
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Started At</dt>
              <dd className="text-sm text-foreground col-span-2">{formatDate(item.started_at)}</dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Created At</dt>
              <dd className="text-sm text-foreground col-span-2">{formatDate(item.created_at)}</dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Updated At</dt>
              <dd className="text-sm text-foreground col-span-2">{formatDate(item.updated_at)}</dd>
            </div>
            {item.canceled_at && (
              <div className="py-3 grid grid-cols-3 gap-4">
                <dt className="text-sm font-medium text-muted-foreground">Canceled At</dt>
                <dd className="text-sm text-destructive col-span-2">{formatDate(item.canceled_at)}</dd>
              </div>
            )}
          </dl>
        </CardContent>
      </Card>
    </div>
  );
});

export default TenantSubscriptionDetailPage;
